import {
  ADD_NEW_JOB,
  SET_TAGS_JOB,
  FORCE_UPDATE_JOB,
  ADD_MESSAGE_TO_JOB_BY_IDENTIFIER,
  ADD_MESSAGE_TO_JOB,
  SET_CLIENT_TYPING,
  SET_CLIENT_STATUS,
  UPDATE_MESSAGE_JOB_BY_CUSTOM_ID,
  ADD_ATTENDANCE_JOB,
} from '@/store/mutation-types';
import ContactService from '@/services/contact-service';
import HistoryMessage from '@/weon/models/HistoryMessage';
import { selectContactByLatestInteraction } from '@/shared/utils/contact-helpers';
import { getActiveJobsFromChannel } from '@/shared/utils/job-helpers';
import {
  qualifyJob,
  closeChatJob,
  getChatQueueById,
  sendMessage,
  chatTyping,
  transfer,
} from '@/services/chat-service';
import { getTagsByProtocol } from '@/services/tag-service';
import { finishChatJob } from '@/services/job-service';
import { getGroups } from '@/services/attendance-groups-service';

const actions = {
  async newChat({ commit, dispatch, rootGetters }, newChat) {
    const rootDispatch = { root: true };
    const agentId = rootGetters['attendance/attendance'].agentId;
    const [contact, queue] = await Promise.all([
      ContactService.getByIdentifier(
        newChat.contactIdentifier,
        'chat_identifier',
        agentId,
        true,
      ),
      getChatQueueById(newChat.chatQueue),
    ]);

    const groups = await getGroups(agentId);
    const attendanceGroupInfo = groups.find(
      (group) => group.id == newChat.attendanceGroupId,
    );
    const observationRequired = attendanceGroupInfo
      ? attendanceGroupInfo.observation_required
      : false;
    newChat.attendanceGroupInfo = { observationRequired };

    let contactSelected = null;

    if (contact.data.length > 1) {
      contactSelected = await selectContactByLatestInteraction(
        contact.data,
        newChat.contactIdentifier,
        'chat',
      );
    } else {
      contactSelected = contact.data;
    }

    const newJob = {
      ...newChat,
      contact: await dispatch('defaultContact', {
        contacts: contactSelected,
        protocol: newChat.protocol,
      }),
      queue,
      tags: [],
      messages: [],
    };

    commit(ADD_NEW_JOB, newJob);

    dispatch('navigation', newJob);

    if (!newJob.closedWithoutQualify) {
      dispatch('channel/updateChatStatus', 'in_attendance', rootDispatch);
    }
    dispatch(
      'showSnackbar',
      { text: 'newChatJob', type: 'success', browserNotify: true },
      rootDispatch,
    );
    dispatch('getTagsChat', newJob.protocol);

    if (!Array.isArray(contact))
      dispatch('contact/lastContact', newJob, { root: true });
  },

  async addNewChat({ commit, dispatch, rootGetters }, newChat) {
    const rootDispatch = { root: true };
    const agentId = rootGetters['attendance/attendance'].agentId;
    const [contact, queue] = await Promise.all([
      ContactService.getByIdentifier(
        newChat.contactIdentifier,
        'chat_identifier',
        agentId,
        true,
      ),
      getChatQueueById(newChat.chatQueue),
    ]);

    const groups = await getGroups(agentId);
    const attendanceGroupInfo = groups.find(
      (group) => group.id == newChat.attendanceGroupId,
    );
    const observationRequired = attendanceGroupInfo
      ? attendanceGroupInfo.observation_required
      : false;
    newChat.attendanceGroupInfo = { observationRequired };

    let contactSelected = null;

    if (contact.data.length > 1) {
      contactSelected = await selectContactByLatestInteraction(
        contact.data,
        newChat.contactIdentifier,
        'chat',
      );
    } else {
      contactSelected = contact.data;
    }

    const newJob = {
      ...newChat,
      contact: await dispatch('defaultContact', {
        contacts: contactSelected,
        protocol: newChat.protocol,
      }),
      queue,
      tags: [],
      messages: newChat.messages || [],
    };

    const contacts = newJob?.contact;

    if (contacts && Array.isArray(contacts) && contacts.length === 0) {
      newJob.contact = {
        lastContact: null,
      };
    }

    commit(ADD_ATTENDANCE_JOB, newJob);

    dispatch('navigation', newJob);

    if (!newJob.closedWithoutQualify) {
      dispatch('channel/updateChatStatus', 'in_attendance', rootDispatch);
    }

    dispatch('getTagsChat', newJob.protocol);
  },

  newMessageChat({ commit }, { message, identifier }) {
    commit(ADD_MESSAGE_TO_JOB_BY_IDENTIFIER, {
      identifier,
      propertyIdentifier: 'from',
      message,
    });
  },

  sendChatMessage({ commit, dispatch }, { protocol, message }) {
    const customId = Math.random().toString(36).substr(2, 10).toUpperCase();
    const customIdSecondary = Math.random()
      .toString(36)
      .substr(2, 10)
      .toUpperCase();
    const url =
      process.env.NODE_ENV == 'development'
        ? process.env.API_URL
        : window.CONFIG.weactionapi.url;
    message.protocol = protocol;
    message.error = false;
    message.id = customId;
    message.idPrimary = customId;
    message.idSecondary = customIdSecondary;

    const messageToSave = JSON.parse(JSON.stringify(message));
    messageToSave.avatar = null;
    if (message.avatar) {
      messageToSave.avatar = url.slice(0, -1) + message.avatar;
    }
    commit(ADD_MESSAGE_TO_JOB, { protocol, message: messageToSave });

    const newMessage = JSON.parse(JSON.stringify(messageToSave));
    newMessage.agent_id = message.from;
    return sendMessage(message).catch((err) => {
      console.log(err);
      newMessage.error = true;
      dispatch(
        'showSnackbar',
        { text: 'Não foi possível enviar a mensagem', type: 'error' },
        { root: true },
      );
      commit(UPDATE_MESSAGE_JOB_BY_CUSTOM_ID, {
        customId,
        customIdSecondary,
        protocol,
        message: newMessage,
      });
    });
  },

  endChat({ dispatch }, { finishJob, protocol }) {
    return finishChatJob(finishJob)
      .then(() => dispatch('disableJob', protocol))
      .catch(console.error);
  },

  getTagsChat({ commit }, protocol) {
    getTagsByProtocol(protocol).then((tags) => {
      commit(SET_TAGS_JOB, { protocol, tags });
      commit(FORCE_UPDATE_JOB, protocol);
    });
  },

  getChatMessagesToday(
    { commit, dispatch },
    { protocol, contactIdentifier, paramsHistoryMessages, channel },
  ) {
    dispatch(
      'contact/getMessageHistory',
      { contactIdentifier, protocol, ...paramsHistoryMessages, channel },
      { root: true },
    )
      .then(({ messages }) => {
        console.log(messages);
        messages = messages.map((message) => {
          return new HistoryMessage({
            from: message.from,
            to: message.to,
            message: message.content.trim(),
            type: message.type,
            time: message.created_at,
          });
        });

        dispatch('jobs/addHistoryMessages', { protocol, messages });
      })
      .catch((err) => {
        console.error(err);
      });
  },

  chatQualify({ dispatch }, informations) {
    return new Promise((resolve, reject) => {
      qualifyJob(informations)
        .then((response) => {
          dispatch(
            'showSnackbar',
            { text: 'qualificationSuccess', type: 'success' },
            { root: true },
          );
          console.log(response);
          resolve(response);
        })
        .catch((err) => {
          dispatch(
            'showSnackbar',
            {
              text: 'Não foi possível realizar a qualificação.',
              type: 'error',
            },
            { root: true },
          );
          reject(err);
        });
    });
  },

  closeChat({ dispatch }, close) {
    return closeChatJob(close);
  },

  updateAgentStatusChat({ dispatch, rootState }) {
    const { jobList } = rootState.jobs;
    const { status } = rootState.channel.channels.chat;
    const jobsChannel = getActiveJobsFromChannel('chat', jobList);
    const doNotHaveJob = jobsChannel.length < 1;
    const attendanceIsNotPaused = status != 'paused';

    if (doNotHaveJob && attendanceIsNotPaused) {
      dispatch('channel/updateChatStatus', 'available', { root: true });
    }
  },

  clientChatTyping({ commit }, { typing, from }) {
    commit(SET_CLIENT_TYPING, { typing, from });
  },

  alterClientStatusChat({ commit }, { from, newStatus }) {
    if (newStatus) {
      commit(SET_CLIENT_STATUS, { from, newStatus });
      return;
    }

    commit(SET_CLIENT_STATUS, { from, newStatus: 'online' });
  },

  sendTypingChat({}, typingEvent) {
    chatTyping(typingEvent).then(console.log).catch(console.error);
  },

  chatTransfer({ dispatch }, job) {
    return new Promise((resolve, reject) => {
      transfer(job)
        .then(resolve)
        .catch((err) => {
          console.log(err);
          const messageAlert = {
            text:
              err.response.data.reason == 'offline'
                ? 'cannotTransferBecauseAgentOffline'
                : `transferError.${err.response.data.reason}`,
            type: 'error',
          };
          dispatch('showSnackbar', messageAlert, { root: true });
          reject(err.message);
        });
    });
  },
};

export default actions;
